import { Controller } from "@hotwired/stimulus"

// Marks "add to project" links with checkmarks when the item is already
// in the current project folder. Listens for sidebar updates (project
// switch, folder switch, item added) and re-scans links.
export default class extends Controller {
  connect() {
    this.currentItems = null
    this.debounceTimer = null

    this.handleSidebarUpdate = this.handleSidebarUpdate.bind(this)
    document.addEventListener('project-items:sidebar-updated', this.handleSidebarUpdate)

    // Watch for new .project-choose-link elements from detail expansion
    // or infinite scroll
    this.observer = new MutationObserver(this.handleMutations.bind(this))
    this.observer.observe(this.element, { childList: true, subtree: true })
  }

  disconnect() {
    document.removeEventListener('project-items:sidebar-updated', this.handleSidebarUpdate)
    if (this.observer) this.observer.disconnect()
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  handleSidebarUpdate() {
    this.fetchAndUpdate()
  }

  handleMutations(mutations) {
    // Check if any added nodes contain .project-choose-link elements
    let hasNewLinks = false
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.classList?.contains('project-choose-link') ||
              node.querySelector?.('.project-choose-link')) {
            hasNewLinks = true
            break
          }
        }
      }
      if (hasNewLinks) break
    }

    if (hasNewLinks) {
      // Debounce to avoid rapid-fire updates during large DOM changes
      if (this.debounceTimer) clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => this.updateLinks(), 100)
    }
  }

  async fetchAndUpdate() {
    try {
      const response = await fetch('/my/projects/current_items.json', {
        headers: { 'Accept': 'application/json' }
      })
      if (response.ok) {
        this.currentItems = await response.json()
        this.updateLinks()
      }
    } catch (error) {
      console.error('Failed to fetch project items:', error)
    }
  }

  updateLinks() {
    if (!this.currentItems) return

    const links = document.querySelectorAll('.project-choose-link')
    links.forEach(link => {
      const href = link.href || link.dataset.originalHref
      if (!href) return

      const url = new URL(href, window.location.origin)
      const masterRecordingId = parseInt(url.searchParams.get('master_recording_id'))
      const recordingMixId = parseInt(url.searchParams.get('recording_mix_id'))

      let inProject = false
      if (masterRecordingId && this.currentItems.master_recording_ids.includes(masterRecordingId)) {
        inProject = true
      }
      if (recordingMixId && this.currentItems.recording_mix_ids.includes(recordingMixId)) {
        inProject = true
      }

      if (inProject) {
        this.markLink(link)
      } else {
        this.resetLink(link)
      }
    })
  }

  markLink(link) {
    if (link.dataset.inProject === 'true') return

    // Store original state
    link.dataset.originalHref = link.href
    link.dataset.originalTitle = link.title
    if (link.dataset.turboFrame) {
      link.dataset.originalTurboFrame = link.dataset.turboFrame
    }
    link.dataset.inProject = 'true'

    // Swap to checkmark
    const icon = link.querySelector('span')
    if (icon) {
      icon.className = 'bi bi-check-circle text-success'
    }
    link.removeAttribute('href')
    delete link.dataset.turboFrame
    link.title = 'Already in project'
    link.style.pointerEvents = 'none'
    link.style.cursor = 'default'

    // Remove facebox controller if present
    if (link.dataset.controller === 'facebox') {
      link.removeAttribute('data-controller')
      link.removeAttribute('data-action')
    }
  }

  resetLink(link) {
    if (link.dataset.inProject !== 'true') return

    // Restore original state
    link.href = link.dataset.originalHref
    link.title = link.dataset.originalTitle
    if (link.dataset.originalTurboFrame) {
      link.dataset.turboFrame = link.dataset.originalTurboFrame
      delete link.dataset.originalTurboFrame
    }

    // Swap back to plus
    const icon = link.querySelector('span')
    if (icon) {
      icon.className = 'bi bi-plus-circle'
    }
    link.style.pointerEvents = ''
    link.style.cursor = ''

    delete link.dataset.inProject
    delete link.dataset.originalHref
    delete link.dataset.originalTitle
  }
}
